import { startCase, lowerFirst } from "lodash";
import type { PillAccent } from "@financeable-com-au/financeable-ui";
import { fCurrencyDecimal, fPercent } from "@/utils/formatNumber";
import { fromMonthlyAmount, toMonthlyAmount } from "@/utils/frequency";
import { type Fee, FeeFrequency, RepaymentFrequency } from "@/store/slices/types/applicationFormSlice";
import { type LoadingItem, type LenderProduct } from "@/providers/LenderCardContextProvider/types/LenderProductTypes";
// @ts-expect-error: ignoring as it is existing js file
import { conditions } from "@/utils/evaluateCriteria";

const isLodgeAISupported = (product: Pick<LenderProduct, "lodgeAI"> | undefined | null): boolean => {
  return !!product?.lodgeAI?.native;
};

const sortLoadingList = (loadingList: LoadingItem[]) =>
  [...loadingList].sort((firstItem, secondItem) => {
    if (firstItem.result && firstItem.result !== secondItem.result) return -1;
    if (firstItem.result === undefined && secondItem.result) return 1;
    if (secondItem.result === undefined && !firstItem.result) return 1;
    if (firstItem.result === secondItem.result) return 0;
    if (!secondItem.result) return -1;
    return 0;
  });

const getLoadingList = (loadingList: LoadingItem[]) =>
  loadingList.map((item) => ({
    ...item,
    formattedValue: item.valueType === "percentage" ? fPercent(item.value) : fCurrencyDecimal(item.value),
  }));

const getFeesList = (fees: Fee[], applicationValues: Record<string, unknown>) => {
  return fees.map((fee) => {
    const result = (fee.criteria ?? []).reduce((value: boolean, criteria) => {
      if (applicationValues[criteria.object]) {
        return conditions[criteria.condition](
          criteria,
          (applicationValues as Record<string, Record<string, string>>)[criteria.object][criteria.attribute] ||
            applicationValues[criteria.object],
        )
          ? value
          : false;
      }
      return false;
    }, true);

    return {
      ...fee,
      formattedValue: fee.calc === "percent" ? fPercent(fee.value ?? 0) : fCurrencyDecimal(fee.value ?? 0),
      key: lowerFirst(startCase(fee.name).replace(/\s/g, "")),
      result,
    };
  });
};

const allAvailableFeesList = [
  {
    name: "Origination fee",
  },
  {
    name: "Document fee",
  },
  {
    name: "Establishment fee",
  },
  {
    name: "Monthly fee",
  },
  {
    name: "Monthly account fee",
  },
  {
    name: "Monthly line fee",
  },
  {
    name: "Inspection fee",
  },
  {
    name: "Account fee",
  },
  {
    name: "Referral fee",
  },
  {
    name: "Payment processing fee",
  },
  {
    name: "PPSR fee",
  },
];

const monthlyFeesList = ["monthlyFee", "monthlyAccountFee", "monthlyLineFee", "accountFee"];

const getSelectableFeesList = (feesList: Fee[]) => {
  const allAvailableFeesListWithProperties: Fee[] = allAvailableFeesList.map((fee) => {
    const feeKey = lowerFirst(startCase(fee.name).replace(/\s/g, ""));
    const isDefaultMonthlyFee = [...monthlyFeesList, "paymentProcessingFee"].includes(feeKey);
    return {
      ...fee,
      key: feeKey,
      value: 0,
      frequency: isDefaultMonthlyFee ? FeeFrequency.Monthly : FeeFrequency.Upfront,
      criteria: [],
    };
  });

  return allAvailableFeesListWithProperties.filter(
    (fee) => !feesList.some((f) => f.name.toUpperCase() === fee.name.toUpperCase()),
  );
};

const defaultRepaymentFeeFrequency = [
  RepaymentFrequency.Weekly,
  RepaymentFrequency.Fortnightly,
  RepaymentFrequency.Monthly,
];

const repaymentFeeFrequency = defaultRepaymentFeeFrequency.map((frequency) => frequency.toLowerCase());

const defaultFeeFrequency = [FeeFrequency.Upfront, FeeFrequency.Financed];

const getFeesFrequencyList = (key: string) => {
  if (key === "paymentProcessingFee") return repaymentFeeFrequency;
  if (monthlyFeesList.includes(key)) return [FeeFrequency.Monthly];
  return defaultFeeFrequency;
};

const getTotalMonthlyPaymentProcessingFee = ({ fees }: { fees: Fee[] }) => {
  const paymentProcessingFee = fees?.find((fee) => fee.key === "paymentProcessingFee");
  if (!paymentProcessingFee) return 0;

  const feeValue = paymentProcessingFee.value || 0;

  // An upfront or financed fee is not recurring, so it falls back to nothing rather than to the fee value.
  return toMonthlyAmount(feeValue, paymentProcessingFee.frequency, 0);
};

const getTotalFeesByFrequency = ({ fees, frequency }: { fees: Fee[]; frequency: FeeFrequency }) =>
  fees.reduce((acc, fee) => {
    if (fee.frequency === frequency && fee.key !== "paymentProcessingFee") {
      return acc + (fee.value ? Number(fee.value) : 0);
    }
    return acc;
  }, 0);

const getDocumentPillColour: Record<string, PillAccent> = {
  "Full doc": 300,
  Adverse: 100,
  "Light doc": 50,
  "Low doc": 400,
  Standard: 200,
};

const getRepaymentsByFrequency = ({
  monthlyRepayments,
  repaymentFrequency,
}: {
  monthlyRepayments: number;
  repaymentFrequency: RepaymentFrequency;
}) => fromMonthlyAmount(monthlyRepayments, repaymentFrequency);

const getWeeklyRepaymentsHavingMonthly = ({ monthlyRepaymentsValue }: { monthlyRepaymentsValue: number }) =>
  fromMonthlyAmount(monthlyRepaymentsValue, RepaymentFrequency.Weekly);

const getFormattedWeeklyRepayments = ({
  repaymentsValue,
  repaymentFrequency,
}: {
  repaymentsValue: number;
  repaymentFrequency: RepaymentFrequency.Monthly;
}) => {
  if (repaymentFrequency === RepaymentFrequency.Monthly) {
    const weeklyRepayments = getWeeklyRepaymentsHavingMonthly({ monthlyRepaymentsValue: repaymentsValue });
    return `${fCurrencyDecimal(weeklyRepayments)}${getRepaymentFrequencyLabel(RepaymentFrequency.Weekly)}`;
  }
  return "";
};

const getMonthlyRepaymentsHavingWeekly = ({ weeklyRepaymentsValue }: { weeklyRepaymentsValue: number }) =>
  toMonthlyAmount(weeklyRepaymentsValue, RepaymentFrequency.Weekly);

const getMonthlyRepaymentsHavingFortnightly = ({
  fortnightlyRepaymentsValue,
}: {
  fortnightlyRepaymentsValue: number;
}) => toMonthlyAmount(fortnightlyRepaymentsValue, RepaymentFrequency.Fortnightly);

const getFormattedMonthlyRepayments = ({
  repaymentsValue,
  repaymentFrequency,
}: {
  repaymentsValue: number;
  repaymentFrequency: RepaymentFrequency.Weekly | RepaymentFrequency.Fortnightly;
}) => {
  switch (repaymentFrequency) {
    case RepaymentFrequency.Weekly: {
      const monthlyRepayments = getMonthlyRepaymentsHavingWeekly({ weeklyRepaymentsValue: repaymentsValue });
      return `${fCurrencyDecimal(monthlyRepayments)}${getRepaymentFrequencyLabel(RepaymentFrequency.Monthly)}`;
    }
    case RepaymentFrequency.Fortnightly: {
      const monthlyRepayments = getMonthlyRepaymentsHavingFortnightly({ fortnightlyRepaymentsValue: repaymentsValue });
      return `${fCurrencyDecimal(monthlyRepayments)}${getRepaymentFrequencyLabel(RepaymentFrequency.Monthly)}`;
    }
  }
};

const getRepaymentFrequencyLabel = (repaymentFrequency: RepaymentFrequency) => {
  switch (repaymentFrequency) {
    case RepaymentFrequency.Weekly:
      return "/wk";
    case RepaymentFrequency.Fortnightly:
      return "/ftn";
    case RepaymentFrequency.Monthly:
      return "/mo";
  }
};

// ## TODO: (FeeFrequency) When the fee frequency has changed, we do not need to convert the lettercase.
const formatFeesForSaveQuote = (fees: Fee[]) =>
  fees.map(({ key, name, value, frequency, referrer }) => ({
    key: key ?? lowerFirst(startCase(name).replace(/\s/g, "")),
    name,
    frequency, // startCase
    value: value ? value.toString() : "",
    ...(referrer !== undefined ? { referrer } : {}),
  }));

// A `mergeWith` customizer that replaces lists outright. `merge` combines lists item by item, so merging a shorter
// fee list would keep the extra fees from the list underneath, bringing back fees removed from a quote.
const replaceArrays = (_objValue: unknown, srcValue: unknown) => (Array.isArray(srcValue) ? srcValue : undefined);

const findKeyValueInData =(data: unknown, targetKey: string): string | undefined => {
  if (typeof data === "object" && data != null) {
    for (const [key, value] of Object.entries(data)) {
      if (key === targetKey) {
        return value;
      }
      const result = findKeyValueInData(value, targetKey);
      if (result !== undefined) {
        return result;
      }
    }
  }
  return undefined;
};

// The desktop app matches the deep link LENDER against Brokerloop lender enums (e.g. ANGLE_AUTO), so use the lender
// on the created session rather than the product's display name (e.g. "Angle Auto").
const getBrokerLoopSessionLender = (data: unknown): string | undefined => {
  const session: unknown = findKeyValueInData(data, "session");
  if (typeof session === "object" && session != null && "lender" in session && typeof session.lender === "string") {
    return session.lender;
  }
  return undefined;
};

export {
  defaultRepaymentFeeFrequency,
  sortLoadingList,
  getLoadingList,
  getFeesList,
  monthlyFeesList,
  getFeesFrequencyList,
  getSelectableFeesList,
  getTotalMonthlyPaymentProcessingFee,
  getTotalFeesByFrequency,
  getDocumentPillColour,
  getRepaymentsByFrequency,
  getWeeklyRepaymentsHavingMonthly,
  getFormattedWeeklyRepayments,
  getFormattedMonthlyRepayments,
  getRepaymentFrequencyLabel,
  formatFeesForSaveQuote,
  replaceArrays,
  findKeyValueInData,
  getBrokerLoopSessionLender,
  isLodgeAISupported,
};
